import { useMemo } from "react";
import { cn } from "web-design-system";
import { Route } from "@/routes/(auth)/(headless)/reports/clients/$id.portfolio-review.$reviewId";
import {
  ExternalHoldingsSummary,
  ExternalSipsSummary,
} from "@/modules/portfolio-review/components/external-holdings-section";
import { Report } from "../components";
import { ReportContainer } from "../components/report-container";
import { ReportFooter } from "../components/report-footer";
import { ReportHeader } from "../components/report-header";
import { CoverSection } from "../components/proposal/cover-section";
import { ReportSection } from "../components/proposal/report-section";
import {
  AmcFundExposurePage,
  HoldingsExposurePage,
} from "../components/portfolio-review/exposure-pages";
import {
  AssetAllocationPage,
  PerformancePage,
  PortfolioOverviewPage,
} from "../components/portfolio-review/overview-pages";
import {
  FundsToExitPage,
  FundsToHoldPage,
  FundsToSwitchPage,
  RecommendationsSummary,
} from "../components/portfolio-review/recommendation-pages";
import { ReportPageTitle } from "../components/portfolio-review/report-blocks";
import {
  externalHoldingsOf,
  leafSectionIds,
  reviewReportSections,
  useAmcNames,
} from "../components/portfolio-review/report-data";

const PAGE = "break-before-page break-inside-avoid";
// Spans several pages, so it must not try to keep them together.
const GROUP = "flex flex-col gap-10 break-inside-auto";

export function PortfolioReviewReport() {
  const {
    client,
    employee,
    distributorProfile,
    distributorBranding,
    review,
    report,
    overview,
  } = Route.useLoaderData();
  const { embed, sections } = Route.useSearch();
  const amcName = useAmcNames(review);
  const external = externalHoldingsOf(overview);

  const available = useMemo(
    () => new Set(leafSectionIds(reviewReportSections({ report }))),
    [report]
  );
  const show = (id: string) =>
    available.has(id) && (!sections || sections.includes(id));

  return (
    <Report embed={!!embed}>
      <style>{`
        @page {
          size: B4 landscape !important;
          margin: 0;
        }
      `}</style>
      <ReportHeader
        distributorBranding={distributorBranding}
        profile={distributorProfile}
      />
      <ReportContainer>
        <div className="content w-full space-y-10 px-8">
          <ReportSection sectionId="cover" visible={show("cover")}>
            <CoverSection
              client={client}
              title="Portfolio Review"
              createdAt={review.created_at ?? review.as_of_date ?? ""}
              distributorProfile={distributorProfile}
              employee={employee}
            />
          </ReportSection>

          <ReportSection
            sectionId="performance"
            className="break-before-page"
            visible={show("performance")}
          >
            <PerformancePage report={report} />
          </ReportSection>

          {/* Each checklist entry toggles one element by id, so a section's
              pages share a wrapper and each page breaks on its own. */}
          <ReportSection
            sectionId="portfolio-overview"
            className={GROUP}
            visible={show("portfolio-overview")}
          >
            <div className={PAGE}>
              <PortfolioOverviewPage
                review={review}
                report={report}
                amcName={amcName}
              />
            </div>
            {external ? (
              <div className={cn(PAGE, "flex flex-col gap-6")}>
                <ReportPageTitle title="Regular vs Direct" />
                <ExternalHoldingsSummary holdings={external} variant="report" />
              </div>
            ) : null}
            {external?.ongoing_sips.sip_count ? (
              <div className={cn(PAGE, "flex flex-col gap-6")}>
                <ReportPageTitle title="Ongoing external SIPs" />
                <ExternalSipsSummary sips={external.ongoing_sips} />
              </div>
            ) : null}
          </ReportSection>

          <ReportSection
            sectionId="recommendations"
            className={cn(PAGE, "flex flex-col gap-8")}
            visible={show("recommendations")}
          >
            <ReportPageTitle title="Recommendations" />
            <RecommendationsSummary review={review} />
            <FundsToSwitchPage review={review} />
            <FundsToExitPage review={review} />
            <FundsToHoldPage review={review} />
          </ReportSection>

          <ReportSection
            sectionId="asset-allocation"
            className="break-before-page"
            visible={show("asset-allocation")}
          >
            <AssetAllocationPage report={report} />
          </ReportSection>

          <ReportSection
            sectionId="exposure"
            className={GROUP}
            visible={show("exposure")}
          >
            <div className={PAGE}>
              <AmcFundExposurePage
                review={review}
                report={report}
                amcName={amcName}
              />
            </div>
            {report.exposure.top_stocks.before.length ? (
              <div className={PAGE}>
                <HoldingsExposurePage
                  title="Stock Exposure"
                  holdings={report.exposure.top_stocks}
                />
              </div>
            ) : null}
            {report.exposure.top_sectors.before.length ? (
              <div className={PAGE}>
                <HoldingsExposurePage
                  title="Sector Exposure"
                  holdings={report.exposure.top_sectors}
                />
              </div>
            ) : null}
          </ReportSection>
        </div>
      </ReportContainer>
      <ReportFooter>{`${client.name}’s Portfolio Review`}</ReportFooter>
    </Report>
  );
}
